/**
 * Partners — listado + crear / editar / eliminar.
 *
 * Alineado con Expogreens-main:
 *   - Tipos: customs_agency | supplier | airline
 *   - Campos: name, nit, address, phone, email (además del type)
 *   - Badge muestra el label ("Proveedor") no la key cruda ("supplier")
 *
 * Los tipos vienen de GET /api/partner-types como [{id,label}].
 */
import { useMemo, useState } from 'react'
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/ui/PageHeader'
import { ExpandableCards, CardField, CardGrid } from '@/components/ui/ExpandableCards'
import { Button } from '@/components/ui/Button'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'
import { cn } from '@/lib/utils'

const EMPTY = {
  name: '',
  type: 'supplier',
  nit: '',
  phone: '',
  email: '',
  address: '',
}

// Colores por tipo (consistentes con v1: supplier=verde, customs=azul,
// airline=púrpura).
const TYPE_PILL = {
  supplier: 'bg-green-100 text-green-800',
  customs_agency: 'bg-blue-100 text-blue-800',
  airline: 'bg-purple-100 text-purple-800',
}

export default function PartnersPage() {
  const { data, loading, error, refresh } = useApi('/partners')
  const types = useApi('/partner-types')
  const [editing, setEditing] = useState(null)
  const [filterType, setFilterType] = useState('all')

  const save = useMutation(async (payload) => {
    if (payload.id) {
      return apiCall(`/partners/${payload.id}`, { method: 'PUT', body: payload })
    }
    return apiCall('/partners', { method: 'POST', body: payload })
  })

  const remove = useMutation(async (id) =>
    apiCall(`/partners/${id}`, { method: 'DELETE' }),
  )

  const typeOptions = Array.isArray(types.data) ? types.data : []
  const labelFor = (id) =>
    typeOptions.find((t) => t.id === id)?.label || id || '—'

  const filtered = useMemo(() => {
    const list = data || []
    if (filterType === 'all') return list
    return list.filter((p) => p.type === filterType)
  }, [data, filterType])

  const onSubmit = async (e) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const payload = Object.fromEntries(fd.entries())
    if (!payload.name?.trim()) {
      return toast.error('El nombre es obligatorio')
    }
    if (editing?.id) payload.id = editing.id
    try {
      await save.mutate(payload)
      toast.success(editing?.id ? 'Partner actualizado' : 'Partner creado')
      setEditing(null)
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onDelete = async (id) => {
    if (!confirm('¿Eliminar este partner?')) return
    try {
      await remove.mutate(id)
      toast.success('Partner eliminado')
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  return (
    <div>
      <PageHeader
        title="Partners"
        subtitle="Proveedores, agencias de aduanas y aerolíneas"
        icon={Building2}
        action={
          <Button onClick={() => setEditing({ ...EMPTY })}>
            <Plus size={20} />
            <span>Nuevo partner</span>
          </Button>
        }
      />

      {/* Filtro por tipo */}
      <div className="mb-4">
        <Select
          className="max-w-xs"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">Todos los tipos</option>
          {typeOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </Select>
      </div>

      {loading && <p className="text-gray-500">Cargando…</p>}
      {error && <p className="text-red-600">Error: {error.message}</p>}
      {data && (
        <ExpandableCards
          items={filtered}
          getKey={(r) => r.id}
          emptyText="Sin partners registrados"
          renderHeader={(r) => (
            <div className="flex items-center gap-4 flex-wrap">
              <span className="font-semibold text-expo-dark">{r.name}</span>
              <span className="text-gray-500 text-sm">{r.id}</span>
              <span
                className={cn(
                  'ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase',
                  TYPE_PILL[r.type] || 'bg-gray-100 text-gray-700',
                )}
              >
                {labelFor(r.type)}
              </span>
            </div>
          )}
          renderDetails={(r) => (
            <div className="space-y-4 pt-4">
              <CardGrid cols={3}>
                <CardField label="ID" value={r.id} />
                <CardField label="Tipo" value={labelFor(r.type)} />
                <CardField label="NIT" value={r.nit || '—'} />
                <CardField label="Teléfono" value={r.phone || '—'} />
                <CardField label="Email" value={r.email || '—'} />
                <CardField label="Dirección" value={r.address || '—'} />
              </CardGrid>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setEditing(r)}>
                  <Pencil size={16} />
                  <span>Editar</span>
                </Button>
                <Button type="button" variant="danger" onClick={() => onDelete(r.id)}>
                  <Trash2 size={16} />
                  <span>Eliminar</span>
                </Button>
              </div>
            </div>
          )}
        />
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Editar partner' : 'Nuevo partner'}
      >
        {editing && (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombre" required>
                <Input name="name" defaultValue={editing.name} required />
              </Field>
              <Field label="Tipo" required>
                <Select name="type" defaultValue={editing.type || 'supplier'}>
                  {typeOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="NIT">
                <Input name="nit" defaultValue={editing.nit} />
              </Field>
              <Field label="Teléfono">
                <Input name="phone" defaultValue={editing.phone} />
              </Field>
            </div>
            <Field label="Email">
              <Input type="email" name="email" defaultValue={editing.email} />
            </Field>
            <Field label="Dirección">
              <Input name="address" defaultValue={editing.address} />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={save.loading}>
                {save.loading ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
